import { trimEnd, trimStart } from "lodash";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import ChatMpProvider from "@/core/data-provider/data-mp/chat/chat-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { useKeyPress } from "@/core/keyPressManager";
import { COMMAND_REG_EXP } from "@/domains/chat/constants/chat";
import { Layer } from "@/domains/chat/constants/layer";
import { IS_DEV } from "@/domains/main/constants/other.ts";
import {
  bModeSelector,
  chatCommandSelector,
  chatIsEnabledSelector,
  chatMessageCacheSelector,
  fastMessageSelector,
  messageSelector,
  openTooltipCommandsSelector, // openTooltipCommandsSelector,
  ownMessageHistoryPositionSelector,
  ownMessageHistorySelector,
  setChatCommand,
  setChatEnable,
  setChatOpen,
  setMessage,
  setMessageCache,
  setOwnMessageHistory,
  setOwnMessageHistoryPosition,
  setTooltipCommandsPosition,
  tooltipCommandsPositionSelector,
  tooltipCommandsSelector, // tooltipCommandsPositionSelector,
  // tooltipCommandsSelector,
} from "@/domains/chat/slice";
import { currLayerSelector } from "@/domains/layer/slice";
import isOocCommand from "@/domains/chat/helpers/isOocCommand";
import useMessagesControl from "@/domains/chat/hooks/useMessagesControl";
import { appliedChatSettingsSelector } from "@/domains/settings/chat/slice";
import KeyCode from "@/assets/constants/key-code";

export default () => {
  const dispatch = useAppDispatch();
  const [chatAgain, setChatAgain] = useState(new Date());
  const currLayer = useAppSelector(currLayerSelector);
  const { isCloseChatAfterSendMessage } = useAppSelector(appliedChatSettingsSelector);
  const isFastMessage = useAppSelector(fastMessageSelector);
  const isEnabled = useAppSelector(chatIsEnabledSelector);
  const isBMode = useAppSelector(bModeSelector);
  const message = useAppSelector(messageSelector);
  const command = useAppSelector(chatCommandSelector);
  const ownMessageHistory = useAppSelector(ownMessageHistorySelector);
  const isOpenTooltipCommands = useAppSelector(openTooltipCommandsSelector);
  const messageHistory = useAppSelector(ownMessageHistorySelector);
  const messageHistoryPosition = useAppSelector(ownMessageHistoryPositionSelector);
  const messageCache = useAppSelector(chatMessageCacheSelector);
  const tooltipCommands = useAppSelector((state) => tooltipCommandsSelector(state, message));
  const tooltipCommandsPosition = useAppSelector(tooltipCommandsPositionSelector);
  const { pushMessage } = useMessagesControl();
  const { subscribeKeyDown, unsubscribeKeyDown } = useKeyPress();

  const disableChat = useCallback(async () => {
    try {
      await ChatMpProvider.inputDisable();
      dispatch(setChatEnable(false));
    } catch (e) {
      console.error(e);
    }
  }, [dispatch]);

  const onMessageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // if (currLayer !== Layer.CHAT) return;
      e.stopPropagation();

      const inputValue = e.target.value;
      let trimmedValue = trimStart(inputValue);

      const caretPosition = e.target.selectionEnd;
      // if (IS_DEV) {
      //   // тут сетаем все команды
      //   const matchCommand = trimmedValue.match(/^\/\S*\s+/);
      //   if (matchCommand) {
      //     const [cmd] = matchCommand;
      //     const findedCommand = trimEnd(cmd.replace("/", ""));
      //     dispatch(setChatCommand(findedCommand));
      //     trimmedValue = trimmedValue.replace(cmd, "");
      //   }
      // } else {
      //   // тут сетаем только те команды которые есть в списке
      //   const findedCommand = findCommand(trimmedValue);
      //   if (findedCommand) {
      //     dispatch(setChatCommand(findedCommand));
      //     trimmedValue = trimmedValue.replace(`/${findedCommand} `, "");
      //   }
      // }
      // тут сетаем все команды
      const matchCommand = trimmedValue.match(/^\/\S*\s+/);
      if (matchCommand && caretPosition === trimmedValue.length) {
        const [cmd] = matchCommand;
        const findedCommand = trimEnd(cmd.replace("/", ""));
        dispatch(setChatCommand(findedCommand));
        trimmedValue = trimmedValue.replace(cmd, "");
      }

      dispatch(setTooltipCommandsPosition(-1));
      dispatch(setMessage(trimmedValue));
    },
    [currLayer, dispatch],
  );

  const onMessageSubmit = useCallback(() => {
    let trimmedMessage = message.trim();
    let findedCommand: string | null = null;

    if (!IS_DEV) {
      mp.invoke("focus", false);
    }

    // надо будет искать команду если мы ищем сообщение через историю, там может быьт команда
    // или если пользователь написал команду без аргументов
    // if (ownMessageHistoryPosition > -1) {
    const matchCommand = trimmedMessage.match(COMMAND_REG_EXP);
    if (matchCommand) {
      const [cmd] = matchCommand;
      findedCommand = trimEnd(cmd.replace("/", ""));
    }
    // }

    trimmedMessage = trimmedMessage.replace(COMMAND_REG_EXP, "");

    if (tooltipCommandsPosition !== -1) {
      const tooltipCommand = tooltipCommands[tooltipCommandsPosition].value;
      dispatch(setChatCommand(tooltipCommand));
      dispatch(setTooltipCommandsPosition(-1));
      dispatch(setMessage(trimmedMessage));
      return;
    }

    //   if (tooltipCommandsPosition !== -1) {
    //     const tooltipCommand = tooltipCommands[tooltipCommandsPosition].value;
    //     dispatch(setChatCommand(tooltipCommand));
    //     dispatch(setTooltipCommandsPosition(-1));
    //   } else if (findedCommand) {
    //     dispatch(setChatCommand(findedCommand));
    //   }

    //   dispatch(setMessage(trimmedMessage));
    //   dispatch(setChatEnable(true));

    if (isFastMessage && isCloseChatAfterSendMessage) {
      dispatch(setChatOpen(false));
    }

    let bCommand = command;
    if (isBMode) {
      const checkCommand = command.endsWith("b") ? command : `${command}b`;
      if (isOocCommand(checkCommand)) {
        bCommand = checkCommand;
      }
    }

    const cmd = findedCommand || bCommand;

    if (!message.length) {
      dispatch(setTooltipCommandsPosition(-1));
      dispatch(setMessage(""));
      dispatch(setChatCommand(""));
      disableChat();

      return;
    }

    const chatNow = new Date();
    if (chatAgain < chatNow) {
      if (cmd) {
        const msg = `${cmd} ${trimmedMessage}`;
        mp.invoke("command", msg);
      } else {
        mp.invoke("chatMessage", trimmedMessage);
      }
      setChatAgain(new Date(chatNow.getTime() + 400));

      // Вызываем mp.invoke для последнего сообщения
      if (!IS_DEV) {
        mp.invoke("setLastMessage", chatNow.getTime());
      }
    } else {
      pushMessage('<span style="color: #cc2121;">[System]</span> Please do not spam messages.');
    }

    const msgForHistory = cmd ? `/${cmd} ${trimmedMessage}` : trimmedMessage;
    if (ownMessageHistory.length) {
      if (ownMessageHistory[0] !== msgForHistory) {
        dispatch(setOwnMessageHistory([msgForHistory, ...ownMessageHistory.slice(0, 99)]));
      }
    } else {
      dispatch(setOwnMessageHistory([msgForHistory, ...ownMessageHistory.slice(0, 99)]));
    }

    dispatch(setMessage(""));
    if (!findedCommand) {
      dispatch(setChatCommand(""));
    }
    dispatch(setOwnMessageHistoryPosition(-1));
    dispatch(setTooltipCommandsPosition(-1));
    disableChat();
  }, [
    chatAgain,
    command,
    dispatch,
    isBMode,
    isCloseChatAfterSendMessage,
    isFastMessage,
    message,
    ownMessageHistory,
    pushMessage,
    tooltipCommands,
    tooltipCommandsPosition,
    disableChat,
  ]);

  const onFocus = useCallback(() => {
    dispatch(setChatEnable(true));
  }, [dispatch]);

  const onBlur = useCallback(() => {
    disableChat();
  }, [disableChat]);

  const onKeyDown = useCallback(
    (keyCode: KeyCode) => {
      if (currLayer !== Layer.CHAT) return;
      if (!isEnabled) return;

      switch (keyCode) {
        case KeyCode.BACKSPACE: {
          if (!message) {
            dispatch(setChatCommand(""));
          }
          break;
        }
        case KeyCode.UP_ARROW:
          if (isOpenTooltipCommands) {
            if (tooltipCommandsPosition <= 0) {
              dispatch(setTooltipCommandsPosition(-1));
            } else {
              dispatch(setTooltipCommandsPosition(tooltipCommandsPosition - 1));
            }
            break;
          }

          dispatch(setChatEnable(true));
          if (messageHistoryPosition < messageHistory.length - 1) {
            if (messageHistoryPosition === -1) {
              dispatch(setMessageCache(message));
            }
            dispatch(setOwnMessageHistoryPosition(messageHistoryPosition + 1));
            dispatch(setMessage(messageHistory[messageHistoryPosition + 1]));
          }
          break;
        case KeyCode.DOWN_ARROW:
          if (isOpenTooltipCommands) {
            if (tooltipCommands.length - 1 !== tooltipCommandsPosition) {
              dispatch(setTooltipCommandsPosition(tooltipCommandsPosition + 1));
            }
            break;
          }

          dispatch(setChatEnable(true));
          if (messageHistoryPosition > -1) {
            if (messageHistoryPosition === 0) {
              dispatch(setMessage(messageCache));
              dispatch(setOwnMessageHistoryPosition(-1));
            } else {
              dispatch(setOwnMessageHistoryPosition(messageHistoryPosition - 1));
              dispatch(setMessage(messageHistory[messageHistoryPosition - 1]));
            }
          }

          break;
        default:
          break;
      }
    },
    [
      currLayer,
      dispatch,
      isEnabled,
      isOpenTooltipCommands,
      message,
      messageCache,
      messageHistory,
      messageHistoryPosition,
      tooltipCommands.length,
      tooltipCommandsPosition,
    ],
  );

  useEffect(() => {
    const key = "chatInput";
    subscribeKeyDown(key, onKeyDown);
    return () => {
      unsubscribeKeyDown(key);
    };
  }, [onKeyDown, subscribeKeyDown, unsubscribeKeyDown]);

  const onChangeCommand = (value: string) => {
    dispatch(setMessage(message.replace(COMMAND_REG_EXP, "")));
    dispatch(setChatCommand(value));
    dispatch(setChatEnable(true));
  };

  return {
    onBlur,
    onFocus,
    onMessageChange,
    onMessageSubmit,
    onKeyDown,
    onChangeCommand,
  };
};
